import { useEffect, useMemo, useState } from "react";
import { Boxes, Search } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PosCard, RankTable } from "./PosTable";
import { fmtInt, fmtNumber } from "@/lib/format";
import { OPS } from "@/constants/testIds";

const PI = OPS.posInsights;

export default function PosItemsPanel({ range, branchId }) {
    const { t, lang } = useI18n();
    const [rows, setRows] = useState(null);
    const [totals, setTotals] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sort, setSort] = useState("revenue");
    const [search, setSearch] = useState("");
    const [debounced, setDebounced] = useState("");

    useEffect(() => {
        const id = setTimeout(() => setDebounced(search.trim()), 350);
        return () => clearTimeout(id);
    }, [search]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get("/pos-insights/items", {
            params: {
                start: range.start, end: range.end, sort, limit: 200,
                ...(branchId !== "all" ? { branch_ids: branchId } : {}),
                ...(debounced ? { search: debounced } : {}),
            },
        }).then((r) => {
            if (!alive) return;
            setRows(r.data.items || []);
            setTotals(r.data.totals || null);
        }).catch(() => { if (alive) { setRows([]); setTotals(null); } })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [range.start, range.end, branchId, sort, debounced]);

    const columns = useMemo(() => ([
        { key: "name", label: t("pi_col_item"), strong: true },
        { key: "category", label: t("pi_col_category") },
        { key: "qty", label: t("pi_col_qty"), type: "num1" },
        { key: "revenue", label: t("pi_col_revenue"), type: "num" },
        { key: "avg_price", label: t("pi_col_avg_price"), type: "num" },
        { key: "days", label: t("pi_col_days"), type: "int" },
    ]), [t]);

    return (
        <PosCard
            icon={Boxes}
            title={t("pi_items_title")}
            desc={t("pi_items_desc")}
            right={
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative">
                        <Search className="pointer-events-none absolute inset-y-0 start-2.5 my-auto h-3.5 w-3.5 text-[color:var(--ink-muted)]" />
                        <Input data-testid={PI.itemsSearch} value={search} onChange={(e) => setSearch(e.target.value)}
                            placeholder={t("pi_items_search")} className="h-9 w-[170px] ps-8 text-sm" />
                    </div>
                    <Select value={sort} onValueChange={setSort}>
                        <SelectTrigger data-testid={PI.itemsSort} className="h-9 w-[150px] text-sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="revenue">{t("pi_sort_revenue")}</SelectItem>
                            <SelectItem value="qty">{t("pi_sort_qty")}</SelectItem>
                            <SelectItem value="name">{t("pi_sort_name")}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            }
        >
            {totals ? (
                <div className="mb-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[color:var(--ink-muted)]">
                    <span>{t("pi_kpi_items_qty")}: <b className="tnum text-[color:var(--ink)]"><bdi dir="ltr">{fmtNumber(totals.qty, lang, 0)}</bdi></b></span>
                    <span>{t("pi_col_revenue")}: <b className="tnum text-[color:var(--ink)]"><bdi dir="ltr">{fmtNumber(totals.revenue, lang, 2)}</bdi></b></span>
                    <span>{t("pi_col_item")}: <b className="tnum text-[color:var(--ink)]"><bdi dir="ltr">{fmtInt(totals.distinct_items, lang)}</bdi></b></span>
                    <span>{t("pi_coverage_days")}: <b className="tnum text-[color:var(--ink)]"><bdi dir="ltr">{fmtInt(totals.days_with_data, lang)}</bdi></b></span>
                </div>
            ) : null}
            {loading ? (
                <div className="space-y-2">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
            ) : (
                <RankTable testId={PI.itemsTable} dataset="pos_item_daily" rows={rows || []} columns={columns} shareKey="revenue"
                    rowTestId={(r) => PI.itemRow(r.recipe_id)} />
            )}
        </PosCard>
    );
}
